import React, { ReactNode, useContext, useEffect, useState } from 'react';

import { FatalAttraction as FA } from '@vpr/tracking';

import { useCapability } from '@vimeo/core/hooks/use-capability';
import { AlbumShare } from '@vimeo/share-modal';

import ShareUpsellCard from '../components/ShareUpsellCard';
import { VimeoAlbumInfoResponse } from '../types';
import AlbumConfigContext from './AlbumConfigContext';
import { getRawUploadedImageUrl, hasPreviewParam, isAlbumCustomized } from '../utils/helpers';

interface RenderProps {
  openShare: VoidFunction;
}

interface Props {
  children: (p: RenderProps) => ReactNode;
  token: string;
  recaptchaSiteKey: string;
  album: VimeoAlbumInfoResponse;
  teamRole?: string;
}

/**
 * Container for the Album Share and Description Modal.
 * Renders modals along with child components that can
 * open/close them
 */
function AlbumsModalContainer({ album, recaptchaSiteKey, token, children, teamRole }: Props) {
  const [showModal, setShowModal] = useState(false);
  const { albumId, isOwnerOrAdmin } = useContext(AlbumConfigContext);
  const { capabilities } = useCapability(['canCustomizeAlbums']);

  if (!capabilities) {
    return null;
  }

  const { canCustomizeAlbums } = capabilities;

  const openModal = () => {
    setShowModal(true);
    window.addEventListener('keydown', preventFullScreenHotKey);
  };

  const closeModal = (isSuccess?: boolean) => {
    if (teamRole && !isSuccess) {
      FA.trackClick({
        container: 'team_home_page',
        component: 'share_modal',
        keyword: 'close_share_' + teamRole.toLowerCase(),
      });
    }
    setShowModal(false);
    window.removeEventListener('keydown', preventFullScreenHotKey);
  };

  const preventFullScreenHotKey = (event: KeyboardEvent) => {
    const F_KEY = 70;

    // If modal is open and user is not typing within an input field and hits 'F' key, do nothing.
    if (event.keyCode === F_KEY && showModal && event.currentTarget instanceof HTMLInputElement) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    return () => {
      // Just in case unmount is fired while modal is open
      window.removeEventListener('keydown', preventFullScreenHotKey);
    };
  }, []);

  /**
   * For showing upsell card in share modal:
   * Must be owner who can't customize previewing customization
   */
  const isPreviewingCustomization =
    isOwnerOrAdmin &&
    !canCustomizeAlbums &&
    hasPreviewParam() &&
    isAlbumCustomized({
      ...album,
      logoUrl: album.customLogo ? getRawUploadedImageUrl(album.customLogo) : '',
    });

  /**
   * Don't show email option in share modal if either:
   * 1. Showcase is off-site
   * 2. Showcase is password-protected and viewed by non-owner
   */
  const hideShareByEmail =
    window.location.hostname === album.domain ||
    (!isOwnerOrAdmin && album.privacy.view === 'password');

  return (
    <>
      {children({
        openShare: () => {
          openModal();
        },
      })}

      {/* Share Modal */}
      {showModal && (
        <AlbumShare
          showModal={showModal}
          onDismiss={closeModal}
          hideShareByEmail={hideShareByEmail}
          card={isPreviewingCustomization ? <ShareUpsellCard albumUrl={album.shareLink} /> : null}
          albumId={albumId}
          link={album.shareLink}
          token={token}
          reCaptchaSiteKey={recaptchaSiteKey}
          trackCopy={() => {
            FA.trackClick({
              container: 'share_album_modal',
              component: 'copy_link',
              keyword: 'copy',
            });
            if (teamRole) {
              FA.trackClick({
                container: 'team_home_page',
                component: 'share_modal',
                keyword: 'copy_link_' + teamRole.toLowerCase(),
              });
            }
          }}
          trackSubmit={(message: string) => {
            FA.trackClick({
              container: 'share_album_modal',
              component: 'send',
            });
            if (teamRole) {
              FA.trackClick({
                container: 'team_home_page',
                component: 'share_modal',
                keyword: 'send_email_' + teamRole.toLowerCase(),
              });
              if (message.length > 0) {
                FA.trackClick({
                  container: 'team_home_page',
                  component: 'share_modal',
                  keyword: 'send_message_' + teamRole.toLowerCase(),
                });
              }
            }
          }}
          trackAddEmail={() => {
            if (teamRole) {
              FA.trackClick({
                container: 'team_home_page',
                component: 'share_modal',
                keyword: 'add_email',
              });
            }
          }}
        />
      )}
    </>
  );
}

export default AlbumsModalContainer;
